/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useMemo, useState } from "react";
import { Progress } from "reactstrap";
import { Loading } from "./Loading";

type Step = {
  text: string;
  start: number;
  end: number;
};

type Props = {
  text: { text: string; seconds: number }[];
  startIndex?: number;
  minHeight?: string;
};

export default function LoadingProgressBar({ text, startIndex = 0, minHeight = "70vh" }: Props) {
    const steps: Step[] = useMemo(() => {
        const result: Step[] = [];
        let current = 0;
        for (let i = startIndex; i < text.length; i++) {
            const { seconds } = text[i];
            result.push({
                text: text[i].text,
                start: current,
                end: current + seconds,
            });
            current += seconds;
        }
        return result;
    }, [text, startIndex]);

    const totalDuration = steps.length > 0 ? steps[steps.length - 1].end : 0;
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        if (totalDuration === 0) return () => null;

        const interval = setInterval(() => {
            setElapsed((prev) => {
                const next = prev + 0.25;
                if (next >= totalDuration) {
                    clearInterval(interval);
                    return totalDuration;
                }
                return next;
            });
        }, 250);

        return () => clearInterval(interval);
    }, [totalDuration]);

    const currentStep = steps.find((s) => elapsed >= s.start && elapsed < s.end) ?? steps[steps.length - 1];

    return (
        <div className="d-flex align-items-center" css={css`min-height: ${minHeight};`}>
            <div className="text-center w-100">
                <Loading />
                <Progress animated color="success" value={(elapsed / totalDuration) * 100} />
                <h6 className="text-muted pt-5">{currentStep?.text ?? "Almost done..."}</h6>
            </div>
        </div>
    );
}
